import { fillText } from 'components/invoiceImport/invoiceImportText';
import {
  contactName,
  contactQualifier,
  invoiceSendMailText,
} from 'components/invoiceSendMail/invoiceSendMailText';
import { companyRoleLabel } from 'constants/companyRoles';
import UseContext from 'hooks/useContext';

const CHECKBOX_CLASS_NAME = 'checkbox-primary';

const refKeyOf = (contact) => `${contact.ref.kind}:${contact.ref.id}`;

/**
 * The billing contacts of one company, a box to tick each (JBWEB-1282): whom
 * a billing mail goes to, as the api lists them (`ref`, names, `email`,
 * `label`, `role`). Used by the invoice mail and the account statement mail.
 * The ticks belong to the caller: `isTicked(contact)`, `onToggle(contact)`.
 * `children` comes under the list.
 */
const MailRecipientList = ({
  companyName,
  contacts,
  isTicked,
  onToggle,
  disabled = false,
  children,
}) => {
  const { language } = UseContext();
  const text = invoiceSendMailText(language);
  const title = fillText(text('recipients.title'), { company: companyName });

  const renderContact = (contact) => {
    const roleLabel =
      contact.label === 'member' && contact.role != null
        ? companyRoleLabel(language, contact.role)
        : '';
    return (
      <label
        key={refKeyOf(contact)}
        className="flex items-start gap-3 px-4 py-3 cursor-pointer"
      >
        <input
          type="checkbox"
          className={CHECKBOX_CLASS_NAME}
          checked={isTicked(contact)}
          disabled={disabled}
          onChange={() => onToggle(contact)}
        />
        <span className="flex flex-col">
          <span className="font-medium text-gray-900">
            {contactName(text, contact)}
          </span>
          <span className="text-gray-500">
            {[contact.email, contactQualifier(text, contact, roleLabel)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      </label>
    );
  };

  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <div className="font-medium text-gray-700">{title}</div>
      {contacts.length > 0 ? (
        <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100">
          {contacts.map(renderContact)}
        </div>
      ) : (
        <div className="rounded-lg border border-gray-100 px-4 py-3 text-gray-500">
          {text('recipients.none')}
        </div>
      )}
      {children}
    </section>
  );
};

export default MailRecipientList;
